import { useCallback, useState, useMemo, useRef, type ReactNode } from 'react'
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { WidgetResizeMenu } from './widget-resize-menu'

export interface WidgetConfig {
  id: string
  width: 33 | 50 | 66 | 100
  order: number
}

interface WidgetGridProps {
  layout: WidgetConfig[]
  onReorder: (layout: WidgetConfig[]) => void
  onResize: (id: string, width: 33 | 50 | 66 | 100) => void
  /** Optional "Reset page layout" entry in every widget's kebab menu —
      restores this grid's default arrangement. */
  onResetLayout?: () => void
  children: (id: string, width: 33 | 50 | 66 | 100) => ReactNode
}

interface DragItem {
  type: string
  id: string
  index: number
  width: 33 | 50 | 66 | 100
}

const ITEM_TYPE = 'WIDGET'

function getColSpan(width: number): number {
  switch (width) {
    case 33: return 4
    case 50: return 6
    case 66: return 8
    case 100:
    default: return 12
  }
}

function getWidthClass(width: number) {
  switch (width) {
    case 33: return 'col-span-4'
    case 50: return 'col-span-6'
    case 66: return 'col-span-8'
    case 100:
    default: return 'col-span-12'
  }
}

interface DraggableWidgetProps {
  config: WidgetConfig
  index: number
  moveWidget: (dragIndex: number, hoverIndex: number) => void
  onResize: (width: 33 | 50 | 66 | 100) => void
  onResetLayout?: () => void
  onDragStart: (height: number) => void
  onDragEnd: (didDrop: boolean) => void
  children: ReactNode
}

function DraggableWidget({ config, index, moveWidget, onResize, onResetLayout, onDragStart, onDragEnd, children }: DraggableWidgetProps) {
  const lastMoveTime = useRef(0)

  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: () => {
      const height = elementRef.current?.getBoundingClientRect().height ?? 80
      onDragStart(height)
      return { type: ITEM_TYPE, id: config.id, index, width: config.width }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (_, monitor) => {
      onDragEnd(monitor.didDrop())
    },
  })

  const elementRef = useRef<HTMLDivElement | null>(null)

  const [, drop] = useDrop<DragItem, void, Record<string, never>>({
    accept: ITEM_TYPE,
    hover: (draggedItem, monitor) => {
      if (!monitor.isOver({ shallow: true })) return
      if (draggedItem.index === index) return

      // Throttle moves
      const now = Date.now()
      if (now - lastMoveTime.current < 200) return

      // Require cursor to be past the vertical midpoint of this widget
      const hoverRect = elementRef.current?.getBoundingClientRect()
      const clientOffset = monitor.getClientOffset()
      if (hoverRect && clientOffset) {
        const hoverMiddleY = (hoverRect.bottom - hoverRect.top) / 2
        const hoverClientY = clientOffset.y - hoverRect.top
        // Moving down: only trigger if cursor is below midpoint
        if (draggedItem.index < index && hoverClientY < hoverMiddleY) return
        // Moving up: only trigger if cursor is above midpoint
        if (draggedItem.index > index && hoverClientY > hoverMiddleY) return
      }

      lastMoveTime.current = now
      moveWidget(draggedItem.index, index)
      draggedItem.index = index
    },
  })

  const ref = (node: HTMLDivElement | null) => {
    elementRef.current = node
    if (node) drag(drop(node))
  }

  return (
    <div
      ref={ref}
      className={`${getWidthClass(config.width)} relative transition-all duration-200 ease-in-out`}
      style={{
        cursor: isDragging ? 'grabbing' : 'grab',
        opacity: isDragging ? 0.4 : 1,
        transform: isDragging ? 'scale(0.98)' : 'scale(1)',
      }}
    >
      <div className="relative group h-full">
        <div className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
          <WidgetResizeMenu currentWidth={config.width} onResize={onResize} onResetLayout={onResetLayout} />
        </div>
        <div className="h-full [&>*]:h-full">
          {children}
        </div>
      </div>
    </div>
  )
}

// Drop target for empty row space
function RowSpacer({ colSpan, targetIndex, moveWidget }: { colSpan: number; targetIndex: number; moveWidget: (dragIndex: number, hoverIndex: number) => void }) {
  const [{ isOver }, drop] = useDrop<DragItem, void, { isOver: boolean }>({
    accept: ITEM_TYPE,
    hover: (draggedItem) => {
      if (draggedItem.index !== targetIndex) {
        moveWidget(draggedItem.index, targetIndex)
        draggedItem.index = targetIndex
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
    }),
  })

  const colClassMap: Record<number, string> = { 2: 'col-span-2', 4: 'col-span-4', 6: 'col-span-6', 8: 'col-span-8', 12: 'col-span-12' }
  const colClass = colClassMap[colSpan] || 'col-span-4'

  return (
    <div ref={drop} className={`${colClass} min-h-[60px] transition-all duration-150`}>
      <div className={`h-full w-full rounded-lg min-h-[60px] border-2 border-dashed transition-all duration-150 ${
        isOver
          ? 'border-[#290BD9]/50 bg-[#290BD9]/5'
          : 'border-transparent'
      }`} />
    </div>
  )
}

function WidgetGridInner({ layout, onReorder, onResize, onResetLayout, children }: WidgetGridProps) {
  const [dragPreview, setDragPreview] = useState<WidgetConfig[] | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [draggedId, setDraggedId] = useState<string | null>(null)
  const [draggedHeight, setDraggedHeight] = useState<number | null>(null)

  const sortedLayout = useMemo(() =>
    [...layout].sort((a, b) => a.order - b.order),
    [layout]
  )

  const displayLayout = dragPreview || sortedLayout

  const moveWidget = useCallback((dragIndex: number, hoverIndex: number) => {
    setDragPreview(prev => {
      const source = prev || sortedLayout
      const updated = [...source]
      const dragged = updated[dragIndex]
      updated.splice(dragIndex, 1)
      updated.splice(hoverIndex, 0, dragged)
      return updated.map((w, i) => ({ ...w, order: i }))
    })
  }, [sortedLayout])

  const handleDragStart = useCallback(() => {
    setIsDragging(true)
    setDragPreview(sortedLayout)
  }, [sortedLayout])

  const handleDragEnd = useCallback((didDrop: boolean) => {
    setIsDragging(false)
    setDraggedId(null)
    if (didDrop && dragPreview) {
      onReorder(dragPreview)
    }
    setDragPreview(null)
  }, [dragPreview, onReorder])

  // Calculate row spacers for empty space
  const gridItems = useMemo(() => {
    const items: { type: 'widget' | 'spacer'; config?: WidgetConfig; index?: number; colSpan?: number; targetIndex?: number }[] = []
    let rowUsed = 0

    for (let i = 0; i < displayLayout.length; i++) {
      const config = displayLayout[i]
      const span = getColSpan(config.width)

      if (rowUsed > 0 && rowUsed + span > 12) {
        const remaining = 12 - rowUsed
        if (remaining > 0) {
          items.push({ type: 'spacer', colSpan: remaining, targetIndex: i })
        }
        rowUsed = 0
      }

      items.push({ type: 'widget', config, index: i })
      rowUsed += span

      if (rowUsed >= 12) rowUsed = 0
    }

    if (rowUsed > 0 && rowUsed < 12) {
      const remaining = 12 - rowUsed
      items.push({ type: 'spacer', colSpan: remaining, targetIndex: displayLayout.length })
    }

    return items
  }, [displayLayout])

  const [, dropRef] = useDrop({
    accept: ITEM_TYPE,
  })

  return (
    <div ref={dropRef} className="grid grid-cols-12 gap-4">
      {gridItems.map((item, i) => {
        if (item.type === 'spacer') {
          if (!isDragging) return null
          return (
            <RowSpacer
              key={`spacer-${i}`}
              colSpan={item.colSpan!}
              targetIndex={item.targetIndex!}
              moveWidget={moveWidget}
            />
          )
        }

        const config = item.config!
        const content = children(config.id, config.width)
        if (!content) return null

        // A single-widget grid has nothing to reorder, and a lone full-width
        // table is not a widget to the user (ruling 09-09): render it plain —
        // no grab cursor, no kebab, no drag chrome.
        if (displayLayout.length <= 1) {
          return (
            <div key={config.id} className={getWidthClass(config.width)}>
              <div className="h-full [&>*]:h-full">{content}</div>
            </div>
          )
        }

        const isBeingDragged = isDragging && config.id === draggedId

        return (
          <DraggableWidget
            key={config.id}
            config={config}
            index={item.index!}
            moveWidget={moveWidget}
            onResize={(width) => onResize(config.id, width)}
            onResetLayout={onResetLayout}
            onDragStart={(h) => { setDraggedId(config.id); setDraggedHeight(h); handleDragStart() }}
            onDragEnd={handleDragEnd}
          >
            {isBeingDragged ? (
              <div
                className="w-full border-2 border-dashed border-[#290BD9]/50 bg-[#290BD9]/5 rounded-lg"
                style={{ height: draggedHeight ?? 80 }}
              />
            ) : content}
          </DraggableWidget>
        )
      })}
    </div>
  )
}

export function WidgetGrid(props: WidgetGridProps) {
  return (
    <DndProvider backend={HTML5Backend}>
      <WidgetGridInner {...props} />
    </DndProvider>
  )
}
